import type {
  AssistantAgreementDisplay,
  AssistantClientDisplay,
  AssistantDayDisplay,
  AssistantDisplay,
  AssistantHistoryDisplay,
  AssistantJobsDisplay,
  AssistantPicksDisplay,
  AssistantSlotsDisplay,
  AssistantStat,
} from '@fieldops/shared'
import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '../../lib/cn.js'
import { money } from '../../lib/format.js'
import { useJobHref } from '../../lib/job-overlay.js'
import { Icon } from '../Icon.js'

/**
 * What Eco found, drawn. Each reading tool sends the model its facts in words
 * and the person this: a day as a timeline, free times to tap, a history as a
 * bar a year, an agreement as a strip of visits. In the brand's two colours —
 * state is fill and outline (done is Eco Green, live is Charcoal, coming is
 * dashed), and red is only ever a problem. A row that is a job opens it over
 * the screen (`useJobHref`, rule 13); a row that is a choice sends its prompt
 * as if typed. Decision 116.
 */

export interface DisplayProps {
  display: AssistantDisplay
  /** Sends a prompt into the conversation, as if typed. */
  onPick: (prompt: string) => void
  /** A run is going; choices wait. */
  busy: boolean
}

export function Display({ display, onPick, busy }: DisplayProps) {
  switch (display.kind) {
    case 'day':
      return <Day display={display} />
    case 'slots':
      return <Slots display={display} onPick={onPick} busy={busy} />
    case 'history':
      return <History display={display} />
    case 'picks':
      return <Picks display={display} onPick={onPick} busy={busy} />
    case 'jobs':
      return <Jobs display={display} />
    case 'agreement':
      return <Agreement display={display} />
    case 'client':
      return <Client display={display} />
  }
}

function Frame({ title, aside, children, label }: { title: string; aside?: ReactNode; children: ReactNode; label?: string }) {
  return (
    <section
      aria-label={label ?? title}
      className="overflow-hidden rounded-lg border border-ink-200 bg-white shadow-[0_1px_2px_rgb(16_24_40/0.04)]"
    >
      <header className="flex items-center gap-2 border-b border-ink-100 px-3.5 py-2">
        <h3 className="min-w-0 flex-1 truncate font-display text-[14px] font-extrabold text-ink-900">{title}</h3>
        {aside}
      </header>
      <div className="flex flex-col gap-3 px-3.5 py-3">{children}</div>
    </section>
  )
}

/**
 * The first rows of a long list, and a button for the rest. A day of 36
 * bookings was a screen and a half before Eco's two sentences; the picture
 * carries the gist and the rest is a tap away.
 */
function useShortList<T>(items: readonly T[], limit: number) {
  const [all, setAll] = useState(false)
  const hidden = all ? 0 : Math.max(0, items.length - limit)
  return {
    shown: hidden ? items.slice(0, limit) : items,
    more: (noun: string) =>
      items.length > limit ? (
        <button
          type="button"
          aria-expanded={all}
          onClick={() => setAll((open) => !open)}
          className="self-start text-[12.5px] font-semibold text-brand-700 hover:underline"
        >
          {all ? 'Show fewer' : `Show all ${items.length} ${noun}`}
        </button>
      ) : null,
  }
}

function Stats({ stats }: { stats: AssistantStat[] }) {
  if (stats.length === 0) return null
  return (
    <dl className="grid grid-cols-[repeat(auto-fill,minmax(96px,1fr))] gap-1.5">
      {stats.map((stat) => (
        <div
          key={stat.label}
          className={cn(
            'rounded-md px-2.5 py-1.5 ring-1 ring-inset',
            stat.tone === 'problem' ? 'bg-red-50 ring-red-200' : stat.tone === 'good' ? 'bg-brand-50 ring-brand-200' : 'bg-ink-50 ring-ink-200',
          )}
        >
          <dt className="text-[10.5px] font-semibold tracking-wide text-ink-500 uppercase">{stat.label}</dt>
          <dd
            className={cn(
              'text-[14px] font-semibold tabular-nums',
              stat.tone === 'problem' ? 'text-red-700' : stat.tone === 'good' ? 'text-brand-700' : 'text-ink-900',
            )}
          >
            {stat.value}
          </dd>
        </div>
      ))}
    </dl>
  )
}

/* -------------------------------------------------------------------------- */
/* A day                                                                      */
/* -------------------------------------------------------------------------- */

const VISIT_STATE: Record<AssistantDayDisplay['visits'][number]['state'], { label: string; bar: string }> = {
  done: { label: 'Done', bar: 'bg-brand-500' },
  live: { label: 'On site', bar: 'bg-ink-900' },
  coming: { label: 'Coming', bar: 'border-2 border-dashed border-ink-300 bg-white' },
  past: { label: 'Not checked off', bar: 'bg-ink-300' },
  missed: { label: 'No-show', bar: 'bg-red-700' },
  cancelled: { label: 'Cancelled', bar: 'bg-ink-200' },
}

function Day({ display }: { display: AssistantDayDisplay }) {
  const jobHref = useJobHref()
  const visits = useShortList(display.visits, 6)
  const events = useShortList(display.events, 4)
  const people = useShortList(display.people, 6)
  const busiest = Math.max(1, ...display.people.map((person) => person.booked))
  return (
    <Frame title={display.title} aside={<span className="text-[11.5px] text-ink-500 tabular-nums">{display.date}</span>}>
      <Stats stats={display.stats} />

      {display.people.length ? (
        <div className="flex flex-col gap-1" aria-label="Who did what">
          {people.shown.map((person) => (
            <div key={person.name} className="grid grid-cols-[minmax(0,7rem)_1fr_auto] items-center gap-2 text-[12px]">
              <span className="truncate text-ink-700">{person.name}</span>
              <span className="relative h-2.5 overflow-hidden rounded-sm bg-ink-100" aria-hidden="true">
                <span className="absolute inset-y-0 left-0 rounded-sm bg-ink-300" style={{ width: `${(person.booked / busiest) * 100}%` }} />
                <span className="absolute inset-y-0 left-0 rounded-sm bg-brand-500" style={{ width: `${(person.done / busiest) * 100}%` }} />
              </span>
              <span className="text-ink-500 tabular-nums">
                {person.done}/{person.booked}
              </span>
            </div>
          ))}
          {people.more('people')}
        </div>
      ) : null}

      {display.visits.length ? (
        <div className="flex flex-col gap-1">
        <ol className="flex flex-col" aria-label="Bookings">
          {visits.shown.map((visit, index) => {
            const state = VISIT_STATE[visit.state]
            const body = (
              <>
                <span className="w-[4.6rem] shrink-0 pt-px text-[11.5px] text-ink-500 tabular-nums">
                  {visit.start}–{visit.end}
                </span>
                <span className={cn('mt-0.5 h-8 w-1.5 shrink-0 rounded-sm', state.bar)} aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className={cn('block truncate text-[12.5px] font-medium text-ink-900', visit.state === 'cancelled' && 'text-ink-400 line-through')}>
                    {visit.title}
                  </span>
                  <span className="block truncate text-[11.5px] text-ink-500">
                    {[visit.subtitle, visit.people.join(', ') || 'Needs a tech'].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <span className={cn('shrink-0 pt-px text-[11px]', visit.state === 'missed' ? 'font-semibold text-red-700' : 'text-ink-500')}>
                  {state.label}
                </span>
              </>
            )
            return (
              <li key={`${visit.jobId}-${index}`}>
                {visit.jobId ? (
                  <Link to={jobHref(visit.jobId)} className="flex items-start gap-2 rounded-md px-1 py-1 hover:bg-ink-50">
                    {body}
                  </Link>
                ) : (
                  <div className="flex items-start gap-2 px-1 py-1">{body}</div>
                )}
              </li>
            )
          })}
        </ol>
        {visits.more('bookings')}
        </div>
      ) : (
        <p className="text-[12.5px] text-ink-500">Nothing booked.</p>
      )}

      {display.events.length ? (
        <div className="flex flex-col gap-1 border-t border-ink-100 pt-2">
        <ul className="flex flex-col gap-0.5" aria-label="Also">
          {events.shown.map((event, index) => (
            <li key={index} className="flex gap-2 text-[12px]">
              <span className="w-28 shrink-0 font-medium text-ink-700">{event.label}</span>
              {event.jobId ? (
                <Link to={jobHref(event.jobId)} className="min-w-0 truncate text-ink-600 hover:text-ink-900 hover:underline">
                  {event.detail}
                </Link>
              ) : (
                <span className="min-w-0 truncate text-ink-600">{event.detail}</span>
              )}
            </li>
          ))}
        </ul>
        {events.more('of these')}
        </div>
      ) : null}
    </Frame>
  )
}

/* -------------------------------------------------------------------------- */
/* Free times                                                                 */
/* -------------------------------------------------------------------------- */

function Slots({ display, onPick, busy }: { display: AssistantSlotsDisplay; onPick: (prompt: string) => void; busy: boolean }) {
  const [picked, setPicked] = useState<string | null>(null)
  const days = useShortList(display.days, 3)
  return (
    <Frame title={display.title} aside={<span className="text-[11.5px] text-ink-500">{display.minutes} min · tap one</span>}>
      {display.days.length === 0 ? <p className="text-[12.5px] text-ink-500">Nobody is free for that long.</p> : null}
      {days.shown.map((dayRow) => (
        <div key={dayRow.date} className="flex flex-col gap-1.5">
          <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">{dayRow.label}</p>
          <div className="flex flex-wrap gap-1.5">
            {dayRow.slots.map((slot) => {
              const key = slot.pick
              const chosen = picked === key
              return (
                <button
                  key={key}
                  type="button"
                  disabled={busy || (picked !== null && !chosen)}
                  aria-pressed={chosen}
                  onClick={() => {
                    setPicked(key)
                    onPick(slot.pick)
                  }}
                  className={cn(
                    'flex flex-col items-start rounded-md px-2.5 py-1.5 text-left ring-1 ring-inset transition-colors',
                    chosen ? 'bg-ink-900 text-white ring-brand-500' : 'bg-white text-ink-900 ring-ink-300 hover:bg-brand-50 hover:ring-brand-400',
                    'disabled:opacity-50',
                  )}
                >
                  <span className="text-[12.5px] font-semibold tabular-nums">
                    {slot.start}–{slot.end}
                  </span>
                  <span className={cn('max-w-[11rem] truncate text-[11px]', chosen ? 'text-brand-300' : 'text-ink-500')}>{slot.person}</span>
                </button>
              )
            })}
          </div>
        </div>
      ))}
      {days.more('days')}
    </Frame>
  )
}

/* -------------------------------------------------------------------------- */
/* History                                                                    */
/* -------------------------------------------------------------------------- */

function History({ display }: { display: AssistantHistoryDisplay }) {
  const jobHref = useJobHref()
  const rows = useShortList(display.rows, 6)
  const most = Math.max(1, ...display.years.map((year) => year.visits))
  return (
    <Frame
      title={display.title}
      aside={
        <Link to={`/clients/${display.clientId}`} className="text-[12px] font-medium text-brand-700 hover:underline">
          Open client
        </Link>
      }
    >
      {display.years.length ? (
        <figure className="flex flex-col gap-1">
          <div className="flex h-20 items-end gap-1" role="img" aria-label={`Visits a year: ${display.years.map((year) => `${year.year} ${year.visits}`).join(', ')}`}>
            {display.years.map((year) => (
              <div key={year.year} className="flex min-w-0 flex-1 flex-col items-center justify-end gap-0.5">
                <span className="text-[10.5px] text-ink-600 tabular-nums">{year.visits}</span>
                <span className="w-full max-w-9 rounded-t-sm bg-brand-500" style={{ height: `${Math.max(4, (year.visits / most) * 56)}px` }} />
              </div>
            ))}
          </div>
          <div className="flex gap-1 border-t border-ink-200 pt-0.5" aria-hidden="true">
            {display.years.map((year) => (
              <span key={year.year} className="min-w-0 flex-1 text-center text-[10.5px] text-ink-500 tabular-nums">
                {year.year.slice(2) === year.year.slice(-2) ? `’${year.year.slice(2)}` : year.year}
              </span>
            ))}
          </div>
        </figure>
      ) : null}

      <ol className="flex flex-col divide-y divide-ink-100" aria-label="Jobs">
        {rows.shown.map((row) => (
          <li key={row.jobId}>
            <Link to={jobHref(row.jobId)} className="grid grid-cols-[4.8rem_1fr_auto] items-start gap-2 py-1.5 hover:bg-ink-50">
              <span className="text-[11.5px] text-ink-500 tabular-nums">{row.date ?? '—'}</span>
              <span className="min-w-0">
                <span className="block truncate text-[12.5px] font-medium text-ink-900">
                  {row.what} <span className="font-normal text-ink-500">· {row.number}</span>
                </span>
                <span className="block truncate text-[11.5px] text-ink-500">
                  {[row.where, row.people.join(' & ')].filter(Boolean).join(' · ') || '—'}
                </span>
              </span>
              <span className="text-right">
                <span
                  className={cn(
                    'block text-[11px] font-medium',
                    row.outcome === 'Completed' ? 'text-brand-700' : row.outcome === 'Unsuccessful' ? 'text-red-700' : 'text-ink-600',
                  )}
                >
                  {row.outcome}
                </span>
                {row.amountIncTax ? <span className="block text-[11px] text-ink-500 tabular-nums">{money(row.amountIncTax)}</span> : null}
              </span>
            </Link>
          </li>
        ))}
      </ol>
      {rows.more('jobs')}
    </Frame>
  )
}

/* -------------------------------------------------------------------------- */
/* Choices, lists, agreements, clients                                        */
/* -------------------------------------------------------------------------- */

function Picks({ display, onPick, busy }: { display: AssistantPicksDisplay; onPick: (prompt: string) => void; busy: boolean }) {
  const [picked, setPicked] = useState<string | null>(null)
  const rows = useShortList(display.rows, 6)
  return (
    <Frame title={display.title}>
      <ul className="flex flex-col gap-1">
        {rows.shown.map((row) => {
          const chosen = picked === row.id
          return (
            <li key={row.id}>
              <button
                type="button"
                disabled={busy || (picked !== null && !chosen)}
                aria-pressed={chosen}
                onClick={() => {
                  setPicked(row.id)
                  onPick(row.pick)
                }}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left ring-1 ring-inset transition-colors disabled:opacity-50',
                  chosen ? 'bg-ink-900 text-white ring-brand-500' : 'bg-white ring-ink-200 hover:bg-brand-50 hover:ring-brand-400',
                )}
              >
                <span className="min-w-0 flex-1">
                  <span className={cn('block truncate text-[12.5px] font-semibold', chosen ? 'text-white' : 'text-ink-900')}>{row.title}</span>
                  {row.detail ? <span className={cn('block truncate text-[11.5px]', chosen ? 'text-brand-300' : 'text-ink-500')}>{row.detail}</span> : null}
                </span>
                <Icon name={chosen ? 'check' : 'chevronRight'} className={cn('size-4 shrink-0', chosen ? 'text-brand-500' : 'text-ink-400')} />
              </button>
            </li>
          )
        })}
      </ul>
      {rows.more('of them')}
    </Frame>
  )
}

function Jobs({ display }: { display: AssistantJobsDisplay }) {
  const jobHref = useJobHref()
  const rows = useShortList(display.rows, 6)
  return (
    <Frame title={display.title}>
      {display.stats ? <Stats stats={display.stats} /> : null}
      {display.rows.length ? (
        <ul className="flex flex-col divide-y divide-ink-100">
          {rows.shown.map((row, index) => (
            <li key={`${row.jobId}-${index}`}>
              <Link to={jobHref(row.jobId)} className="flex items-start gap-2 py-1.5 hover:bg-ink-50">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[12.5px] font-medium text-ink-900">
                    {row.client} <span className="font-normal text-ink-500">· {row.number}</span>
                  </span>
                  <span className="block truncate text-[11.5px] text-ink-500">{[row.detail, row.when].filter(Boolean).join(' · ')}</span>
                </span>
                <span className="shrink-0 text-right">
                  <span className="block text-[11px] font-medium text-ink-700">{row.stage}</span>
                  {row.amountIncTax ? <span className="block text-[11px] text-ink-500 tabular-nums">{money(row.amountIncTax)}</span> : null}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[12.5px] text-brand-700">Nothing waiting.</p>
      )}
      {rows.more('jobs')}
    </Frame>
  )
}

const STRIP: Record<AssistantAgreementDisplay['visits'][number]['state'], { label: string; cell: string }> = {
  done: { label: 'Done', cell: 'bg-brand-500 ring-brand-600' },
  booked: { label: 'Job made', cell: 'bg-brand-100 ring-brand-500' },
  planned: { label: 'Planned', cell: 'bg-white ring-ink-300 [border-style:dashed]' },
  skipped: { label: 'Skipped', cell: 'bg-ink-100 ring-ink-200' },
  late: { label: 'Late', cell: 'bg-white ring-red-700 ring-2' },
}

function Agreement({ display }: { display: AssistantAgreementDisplay }) {
  const jobHref = useJobHref()
  return (
    <Frame
      title={`${display.client} — ${display.title}`}
      aside={
        <Link to={`/agreements/${display.agreementId}`} className="text-[12px] font-medium text-brand-700 hover:underline">
          Open
        </Link>
      }
    >
      <p className="text-[12px] text-ink-600">
        {display.status === 'ACTIVE' ? 'Active' : display.status === 'PAUSED' ? 'Paused' : 'Ended'} · {display.term}
      </p>
      <Stats stats={display.stats} />
      {display.next ? (
        <p className={cn('rounded-md border-l-[3px] px-2.5 py-1.5 text-[12.5px]', display.next.late ? 'border-red-700 bg-red-50 text-red-800' : 'border-brand-500 bg-brand-50 text-ink-800')}>
          <strong className="font-semibold">{display.next.late ? 'Late: ' : 'Next: '}</strong>
          {display.next.service ?? 'Visit'} due {display.next.dueOn} at {display.next.site}
        </p>
      ) : null}
      {display.visits.length ? (
        <div className="flex flex-col gap-1.5">
          <ol className="flex flex-wrap gap-1" aria-label="Visits this term">
            {display.visits.map((visit) => {
              const cell = STRIP[visit.state]
              const title = `${visit.dueOn} · ${visit.service} · ${cell.label}`
              const square = <span className={cn('block size-5 rounded-sm ring-1 ring-inset', cell.cell)} />
              return (
                <li key={`${visit.scheduleId}-${visit.sequence}`} title={title} aria-label={title}>
                  {visit.jobId ? <Link to={jobHref(visit.jobId)}>{square}</Link> : square}
                </li>
              )
            })}
          </ol>
          <p className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10.5px] text-ink-500" aria-hidden="true">
            {Object.values(STRIP).map((entry) => (
              <span key={entry.label} className="inline-flex items-center gap-1">
                <span className={cn('inline-block size-2.5 rounded-[2px] ring-1 ring-inset', entry.cell)} />
                {entry.label}
              </span>
            ))}
          </p>
        </div>
      ) : null}
    </Frame>
  )
}

function Client({ display }: { display: AssistantClientDisplay }) {
  return (
    <Frame
      title={display.title}
      aside={
        <Link to={`/clients/${display.clientId}`} className="text-[12px] font-medium text-brand-700 hover:underline">
          Open client
        </Link>
      }
    >
      <Stats stats={display.stats} />
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <p className="mb-1 text-[11px] font-semibold tracking-wide text-ink-500 uppercase">People</p>
          <ul className="flex flex-col gap-1">
            {display.people.map((person) => (
              <li key={person.name} className="text-[12.5px]">
                <span className="font-medium text-ink-900">{person.name}</span>
                {person.detail ? <span className="block truncate text-[11.5px] text-ink-500">{person.detail}</span> : null}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="mb-1 text-[11px] font-semibold tracking-wide text-ink-500 uppercase">Properties</p>
          <ul className="flex flex-col gap-1">
            {display.sites.map((site) => (
              <li key={site.name} className="text-[12.5px]">
                <span className="font-medium text-ink-900">{site.name}</span>
                {site.detail ? <span className="block text-[11.5px] text-ink-500">{site.detail}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Frame>
  )
}
